import { useMemo, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Shell from "../components/Shell";
import VertragVorschau from "../components/VertragVorschau";
import FassungKopf from "../components/FassungKopf";
import Paragrafen from "../components/KlauselParagrafen";
import {
  BLAETTER, BLATT_LABEL, STATUS_LABEL, VORSCHAU_START, fehlertext, gliederung, marken,
  useFassung, useFassungAnlegen, useFassungAusVorlage,
  useFassungVerwerfen, useFassungen, useFreigeben, usePruefen, useWerkzeugkasten,
} from "../hooks/useVertragstext";
import type { Blatt, VorschauSchalter } from "../hooks/useVertragstext";
import { EntwurfKontext, useEntwuerfe } from "../hooks/useEntwuerfe";
import "../styles/vertragstext.css";

/** Was eine Seite zeigt. Vertrag und AGB sind seit dem 28.09.2026 zwei Seiten
 *  (Benjamin: „für die AGB braucht es gesondert eine Seite") — aber eine
 *  Fassung, weil sie nur zusammen freigegeben werden können. */
type Modus = "vertrag" | "agb";
const SEITE: Record<Modus, { titel: string; blaetter: Blatt[] }> = {
  vertrag: { titel: "Vertragstext", blaetter: BLAETTER.filter(b => b !== "agb") },
  agb: { titel: "AGB", blaetter: ["agb"] },
};

/**
 * Vertragstext — die §§ des Jahrespflegevertrages redigieren.
 *
 * Bis hierher stand der Vertragstext als Jinja-Vorlage im Repo; jede
 * Formulierung war ein Deploy. Konzept: docs/KONZEPT-VERTRAGSEDITOR.md.
 *
 * Drei Dinge, die diese Seite bewusst NICHT anbietet:
 *
 * - **Nummern eintippen.** Weder `§ 7` noch `(3)`. Beides entsteht aus der
 *   Reihenfolge; Querverweise werden als Marke gesetzt. Sonst zeigte jedes
 *   Umsortieren still auf den falschen Paragrafen.
 * - **Tabellen bauen.** Leistungsverzeichnis, Bestand, Summen und
 *   Laufzeit-Aufstellung sind Bausteine: sie werden platziert, nicht bearbeitet.
 *   Sie rechnen, runden und unterscheiden netto von brutto.
 * - **Eine freigegebene Fassung ändern.** An ihr hängen unterschriebene
 *   Verträge. Wer sie ändern will, kopiert sie und gibt die Kopie frei.
 */
export default function VertragstextPage() {
  return <KlauselwerkSeite modus="vertrag" />;
}

/** Vertrag und AGB sind ein Eintrag in der Leiste („Vertragstext und AGB")
 *  und zwei Seiten (Benjamin, 28.09.2026: die AGB brauchen eine eigene Seite). */
function BlattWahl({ modus }: { modus: Modus }) {
  return (
    <div className="pf-chips" role="group" aria-label="Blatt wählen" style={{ marginBottom: 0 }}>
      <Link to="/vertragstext" className={"pf-chip" + (modus === "vertrag" ? " is-on" : "")}
        aria-current={modus === "vertrag" ? "page" : undefined} style={{ textDecoration: "none" }}>Vertrag</Link>
      <Link to="/bedingungen" className={"pf-chip" + (modus === "agb" ? " is-on" : "")}
        aria-current={modus === "agb" ? "page" : undefined} style={{ textDecoration: "none" }}>AGB</Link>
    </div>
  );
}

export function KlauselwerkSeite({ modus }: { modus: Modus }) {
  const seite = SEITE[modus];
  const entwuerfe = useEntwuerfe();
  const speichertGerade = useIsMutating() > 0;
  const { data: uebersicht, isLoading } = useFassungen();
  const { data: werkzeug } = useWerkzeugkasten();

  const fassungen = uebersicht?.fassungen || [];
  const entwurf = fassungen.find(f => f.status === "entwurf");
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const werkId = gewaehlt || entwurf?.id || uebersicht?.aktuelle_id || fassungen[0]?.id || null;

  const { data: fassung } = useFassung(werkId);
  const [schalter, setSchalter] = useState<VorschauSchalter>(VORSCHAU_START);
  const [zeigeVorschau, setZeigeVorschau] = useState(false);
  const [blatt, setBlatt] = useState<Blatt>(seite.blaetter[0]);

  const ausVorlage = useFassungAusVorlage();
  const anlegen = useFassungAnlegen();
  const verwerfen = useFassungVerwerfen();
  const freigeben = useFreigeben(werkId);
  const pruefen = usePruefen(werkId);

  const gruppen = useMemo(() => gliederung(fassung?.klauseln || []), [fassung]);
  // Die Marken kommen aus ALLEN Blättern: ein Absatz der Urkunde verweist auf
  // einen Paragrafen der AGB, und umgekehrt. Eine Palette, die nur das
  // sichtbare Blatt kennt, verbietet genau die Verweise, die der Umbau braucht.
  const markenListe = useMemo(() => marken(gruppen), [gruppen]);
  const sichtbar = useMemo(() => gruppen.filter(g => g.dokument === blatt), [gruppen, blatt]);
  const schreibbar = fassung?.bearbeitbar ?? fassung?.status === "entwurf";
  const blattInfo = werkzeug?.blaetter?.find(b => b.name === blatt);

  if (isLoading) return <Shell title={seite.titel} action={<BlattWahl modus={modus} />}><div className="pf-empty">Lade…</div></Shell>;

  if (fassungen.length === 0) {
    return (
      <Shell title={seite.titel} subtitle="Noch keine Fassung angelegt">
        <div className="pf-card vt-einstieg">
          <p>
            Der Vertragstext steht heute als Vorlage im Code — jede Formulierung ist ein
            Deploy. Der erste Schritt liest ihn einmal ein: 14 Paragrafen, Anhang A und B,
            Wort für Wort derselbe Text, den deine Verträge heute tragen.
          </p>
          <p className="vt-hint">
            Danach änderst du ihn hier. Bis eine Fassung freigegeben ist, bleibt die
            Vorlage die Quelle — es kann also nichts kaputtgehen.
          </p>
          <button className="pf-btn" disabled={ausVorlage.isPending}
                  onClick={() => ausVorlage.mutate()}>
            Bestandstext einlesen
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <EntwurfKontext.Provider value={entwuerfe}>
    <Shell
      breit={zeigeVorschau}
      title={seite.titel}
      subtitle={uebersicht?.vorlage_ist_quelle
        ? "Noch keine Fassung in Kraft — Verträge nutzen weiterhin die Vorlage im Code"
        : `In Kraft: V${fassungen.find(f => f.id === uebersicht?.aktuelle_id)?.version ?? "?"}`}
      action={
        <>
          <BlattWahl modus={modus} />
          <button className="pf-btn is-ghost is-small" onClick={() => setZeigeVorschau(v => !v)}>
            {zeigeVorschau ? "Vorschau zu" : "Vorschau"}
          </button>
        </>
      }
    >
      <div className="vt-fassungen">
        {fassungen.map(f => (
          <button
            key={f.id}
            className={"vt-fassung" + (f.id === werkId ? " is-on" : "")}
            onClick={() => setGewaehlt(f.id)}
          >
            <span className="vt-fassung-nr">V{f.version}</span>
            <span className={"pf-pill is-" + f.status}>{STATUS_LABEL[f.status]}</span>
            <span className="vt-fassung-meta">
              {f.absaetze} Absätze
              {f.gilt_ab ? ` · ab ${new Date(f.gilt_ab).toLocaleDateString("de-DE")}` : ""}
              {f.vertraege ? ` · ${f.vertraege} Verträge` : ""}
            </span>
          </button>
        ))}
        {!entwurf && (
          <button
            className="pf-btn is-ghost is-small"
            disabled={anlegen.isPending}
            onClick={() => anlegen.mutate(
              { quelle_id: werkId, notiz: "" },
              { onSuccess: (neu) => setGewaehlt((neu as { id: string }).id) },
            )}
          >
            ＋ Neue Fassung aus dieser
          </button>
        )}
      </div>

      {fassung && (
        <FassungKopf
          status={fassung.status}
          bearbeitbar={!!schreibbar}
          version={fassung.version}
          vertraege={fassung.vertraege}
          onPruefen={() => pruefen.mutate()}
          pruefErgebnis={pruefen.data}
          onFreigeben={(gilt_ab) => freigeben.mutate({ gilt_ab })}
          onVerwerfen={() => verwerfen.mutate(fassung.id, { onSuccess: () => setGewaehlt(null) })}
          offen={entwuerfe.anzahl}
          speichertGerade={speichertGerade}
          onSpeichern={entwuerfe.alleSichern}
          amArbeiten={freigeben.isPending || verwerfen.isPending || pruefen.isPending}
          fehler={freigeben.error ? fehlertext(freigeben.error)
                : verwerfen.error ? fehlertext(verwerfen.error) : undefined}
        />
      )}

      {/* Die Blätter des Dokumentensatzes. Ein Reiter je Blatt und nicht eine
          lange Liste: Urkunde und AGB zählen für sich, und wer sie
          untereinander sieht, hält § 1 der AGB für die Fortsetzung von
          Ziffer 9 der Urkunde. */}
      {seite.blaetter.length > 1 && (
      <div className="vt-blaetter" role="tablist">
        {seite.blaetter.map(b => {
          const anzahl = gruppen.filter(g => g.dokument === b).length;
          return (
            <button
              key={b} role="tab" aria-selected={b === blatt}
              className={"vt-blatt" + (b === blatt ? " is-on" : "")}
              onClick={() => setBlatt(b)}
            >
              <span className="vt-blatt-name">{BLATT_LABEL[b]}</span>
              <span className="vt-blatt-meta">
                {anzahl} {anzahl === 1 ? "Abschnitt" : "Abschnitte"}
              </span>
            </button>
          );
        })}
      </div>
      )}
      {blattInfo && <div className="vt-blatt-hinweis">{blattInfo.hinweis}</div>}

      <div className={"vt-buehne" + (zeigeVorschau ? " mit-vorschau" : "")}>
        <div className="vt-editor">
          {werkId && (
            <Paragrafen
              /* Fassungs- ODER Blattwechsel setzt den Klapp-Zustand zurück:
                 die Abschnitte eines anderen Blattes sind andere Abschnitte. */
              key={`${werkId}-${blatt}`}
              werkId={werkId} blatt={blatt} gruppen={sichtbar} marken={markenListe}
              werkzeug={werkzeug} schreibbar={!!schreibbar}
            />
          )}
        </div>
        {zeigeVorschau && (
          <VertragVorschau werkId={werkId} blatt={blatt}
                           schalter={schalter} onSchalter={setSchalter} />
        )}
      </div>
    </Shell>
    </EntwurfKontext.Provider>
  );
}
